import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col } from 'react-bootstrap';
import moment from 'moment/min/moment-with-locales';

import applicationRouter from '~/hoc/applicationRouter';
import withLocalization from '~/hoc/withLocalization';
import Button from '../../../components/CustomButton/CustomButton.jsx';
import LoadingSpinner from '../../../elements/LoadingSpinner';

import '../../../styles/scss/visitor.scss';

const CATEGORY_COLOR = {
    health: '#ef4444',
    safety: '#f59e0b',
    security: '#2769f6',
    other: '#10b981',
};

function categoryBadge(cat) {
    const c = (cat || 'other').toLowerCase();
    const color = CATEGORY_COLOR[c] || '#6b7280';
    return (
        <span
            className="vld-category-badge"
            style={{ background: color + '1a', color, border: `1px solid ${color}` }}
        >
            {c}
        </span>
    );
}

class VisitorLogDetail extends Component {
    constructor(props) {
        super(props);
        this.state = {
            logDetail: null,
            loading: true,
            approveNotes: '',
            pmNotes: '',
            actionLoading: false,
            pmReviewerNames: null,
            approvedByName: null,
            downloadingPass: false,
        };
    }

    componentDidMount() {
        this.loadDetail();
    }

    loadDetail = async () => {
        const id = this.props.router.params.id;
        try {
            const data = await this.props.visitorStore.getVisitorLogById(id);
            this.setState({ logDetail: data.log, pmReviewerNames: data.pmReviewerNames || null, approvedByName: data.approvedByName || null, loading: false });
        } catch (e) {
            this.setState({ loading: false });
        }
    };

    handleApprove = async () => {
        const { visitorStore, commonStore, t } = this.props;
        const { logDetail, approveNotes } = this.state;
        this.setState({ actionLoading: true });
        try {
            await visitorStore.approveVisitorLog(logDetail.id, approveNotes);
            commonStore.addNotification(t('Visitor Approved'), null, 'success');
            this.loadDetail();
        } catch (e) {
            const msg = (e.response && e.response.body && e.response.body.error) || t('Error');
            commonStore.addNotification(msg, null, 'error');
        }
        this.setState({ actionLoading: false });
    };

    handleReject = async () => {
        const { visitorStore, commonStore, t } = this.props;
        const { logDetail, approveNotes } = this.state;
        this.setState({ actionLoading: true });
        try {
            await visitorStore.rejectVisitorLog(logDetail.id, approveNotes);
            commonStore.addNotification(t('Visitor Rejected'), null, 'error');
            this.loadDetail();
        } catch (e) {
            commonStore.addNotification(t('Error'), null, 'error');
        }
        this.setState({ actionLoading: false });
    };

    handlePmReview = async (decision) => {
        const { visitorStore, commonStore, t } = this.props;
        const { logDetail, pmNotes } = this.state;
        this.setState({ actionLoading: true });
        try {
            await visitorStore.pmReviewVisitorLog(logDetail.id, decision, pmNotes);
            commonStore.addNotification(
                decision === 'approved' ? t('PM Approved') : t('PM Rejected'),
                null,
                decision === 'approved' ? 'success' : 'error'
            );
            this.loadDetail();
        } catch (e) {
            commonStore.addNotification(t('Error'), null, 'error');
        }
        this.setState({ actionLoading: false });
    };

    handleCheckout = async () => {
        const { visitorStore, commonStore, t } = this.props;
        const { logDetail } = this.state;
        this.setState({ actionLoading: true });
        try {
            await visitorStore.checkoutVisitorLog(logDetail.id);
            commonStore.addNotification(t('Checked out'), null, 'success');
            this.loadDetail();
        } catch (e) {
            commonStore.addNotification(t('Error'), null, 'error');
        }
        this.setState({ actionLoading: false });
    };

    handleDownloadPass = () => {
        const { visitorStore } = this.props;
        const { logDetail } = this.state;
        visitorStore.downloadVisitorPass(logDetail.id);
    };

    goBack = () => {
        this.props.router.navigate('/admin/visitorLog');
    };

    renderSafetyResponses(responses) {
        const { t } = this.props;
        if (!responses || responses.length === 0) return null;

        // Group by category
        const groups = {};
        responses.forEach((resp) => {
            const cat = (resp.rule && resp.rule.category) || 'other';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(resp);
        });

        return (
            <>
                {Object.entries(groups).map(([cat, responses]) => (
                    <div key={cat} className="mb-4">
                        <div className="mb-2">{categoryBadge(cat)}</div>
                        <table className="table table-bordered table-sm mb-0">
                            <thead className="thead-light">
                                <tr>
                                    <th className="vld-th-question">{t('Question')}</th>
                                    <th className="vld-th-answer">{t('Answer')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {responses.map((resp) => {
                                    const isTriggered = resp.rule && resp.rule.trigger_alert && resp.answer === 'yes';
                                    return (
                                        <tr key={resp.id} className={isTriggered ? 'vld-triggered-row' : undefined}>
                                            <td className="vld-td-question">
                                                {isTriggered && (
                                                    <i className="fa fa-exclamation-triangle mr-1 vld-trigger-icon" />
                                                )}
                                                {resp.rule ? resp.rule.question : '-'}
                                            </td>
                                            <td className="vld-td-answer">
                                                <span className={`vld-answer vld-answer--${resp.answer === 'yes' ? 'yes' : 'no'}`}>
                                                    {resp.answer ? resp.answer.toUpperCase() : '-'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ))}
            </>
        );
    }

    renderPmReviewSection(logDetail) {
        const { t, userStore } = this.props;
        const { pmNotes, actionLoading, pmReviewerNames } = this.state;
        const pmData = logDetail.data || {};
        if (!pmData.requires_pm_review) return null;

        const pmStatus = pmData.pm_review_status || 'pending';
        const currentUser = userStore.currentUser || {};
        const userType = currentUser.user_type;
        const assignedIds = pmData.pm_reviewer_ids || [];
        const isPm = userType === 'admin' || assignedIds.some((id) => String(id) === String(currentUser.id));
        const statusColors = {
            pending: { bg: '#fff8f0', border: '#f59e0b', text: '#b45309', label: t('Pending PM Review') },
            approved: { bg: '#f0fdf4', border: '#22c55e', text: '#16a34a', label: t('PM Approved') },
            rejected: { bg: '#fef2f2', border: '#ef4444', text: '#dc2626', label: t('PM Rejected') },
        };
        const sc = statusColors[pmStatus] || statusColors.pending;

        return (
            <div className="form-group field field-string w-100 mb-3">
                <label className="control-label fs15">{t('PM Review')}</label>
                <div
                    style={{ background: sc.bg, border: `1px solid ${sc.border}`, borderRadius: 8, padding: '14px 16px', marginTop: 6 }}
                >
                    <div className="d-flex align-items-center mb-2 vld-pm-header-row">
                        <span style={{ fontWeight: 700, fontSize: 13, color: sc.text }}>{sc.label}</span>
                        {pmData.pm_reviewer_name && pmStatus !== 'pending' && (
                            <span className="vld-pm-reviewer">
                                {t('by')} {pmData.pm_reviewer_name}
                            </span>
                        )}
                    </div>
                    {pmStatus === 'pending' && pmReviewerNames && (
                        <div className="vld-pm-waiting">
                            <i className="fa fa-user mr-1" />
                            {t('Waiting for approval from')}: <strong>{pmReviewerNames}</strong>
                        </div>
                    )}
                    {pmData.pm_review_notes && (
                        <div className="vld-pm-notes-text">
                            {t('Notes')}: {pmData.pm_review_notes}
                        </div>
                    )}

                    {pmStatus === 'pending' && isPm && (
                        <>
                            {assignedIds.some((id) => String(id) === String(currentUser.id)) && (
                                <div className="d-flex align-items-center mb-3 vld-pm-assigned-box">
                                    <i className="fa fa-user-circle vld-pm-assigned-icon" />
                                    <span className="vld-pm-assigned-text">
                                        <strong>{t('You are assigned to review this visitor.')}</strong>{' '}
                                        {t('Please check the highlighted answers and take action.')}
                                    </span>
                                </div>
                            )}
                            <div className="alert alert-warning d-flex align-items-center mb-3 vld-pm-alert">
                                <i className="fa fa-bell vld-pm-bell-icon" />
                                <span>
                                    <strong>{t('Action Required')}: </strong>
                                    {t(
                                        'Please review the highlighted answers above and approve or reject this visitor.'
                                    )}
                                </span>
                            </div>
                            <div className="form-group field field-string w-100 mb-2">
                                <label className="control-label vld-sm-label">
                                    {t('PM Notes (optional)')}
                                </label>
                                <textarea
                                    className="form-control"
                                    rows={2}
                                    maxLength={500}
                                    value={pmNotes}
                                    onChange={(e) => this.setState({ pmNotes: e.target.value })}
                                    placeholder={t('Add notes...')}
                                />
                            </div>
                            <div className="d-flex vld-pm-btns">
                                <Button
                                    fill
                                    wd
                                    onClick={() => this.handlePmReview('approved')}
                                    disabled={actionLoading}
                                >
                                    <i className="fa fa-check" /> {t('PM Approve')}
                                </Button>
                                <Button onClick={() => this.handlePmReview('rejected')} disabled={actionLoading}>
                                    <i className="fa fa-times" /> {t('PM Reject')}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { t } = this.props;
        const { logDetail, loading, approveNotes, actionLoading, approvedByName, downloadingPass } = this.state;

        if (loading) {
            return (
                <div className="table-list">
                    <div className="d-flex justify-content-center align-items-center vld-loading-wrap">
                        <LoadingSpinner />
                    </div>
                </div>
            );
        }

        if (!logDetail) {
            return (
                <div className="table-list">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="mb-0 font-weight-bold">{t('Visitor Log Details')}</h4>
                        <Button onClick={this.goBack}>{t('Go Back')}</Button>
                    </div>
                    <div className="text-center p-5 text-muted">{t('No data found')}</div>
                </div>
            );
        }

        const status = logDetail.status || 'pending';
        const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);
        const isPending = status === 'pending';
        const canCheckout = status === 'approved' && !logDetail.check_out_time;

        const pmData = logDetail.data || {};
        const infoRows = [
            { label: t('Visitor Name'), value: logDetail.visitor_name },
            { label: t('Email Address'), value: logDetail.visitor_email || '-' },
            { label: t('Mobile Number'), value: logDetail.visitor_phone || '-' },
            { label: t('Company'), value: logDetail.visitor_company || '-' },
            { label: t('Zone'), value: logDetail.zone ? logDetail.zone.name : '-' },
            { label: t('Contact Person'), value: logDetail.contact_person_name || '-' },
            { label: t('Purpose'), value: logDetail.purpose_of_visit || '-' },
            {
                label: t('Check In'),
                value: logDetail.check_in_time ? moment(logDetail.check_in_time).format('MMM D, YYYY HH:mm') : '-',
            },
            {
                label: t('Check Out'),
                value: logDetail.check_out_time ? moment(logDetail.check_out_time).format('MMM D, YYYY HH:mm') : '-',
            },
            {
                label: t('Status'),
                value: (
                    <span className={`vl-status-block vl-status-${status} vld-status-badge`}>
                        {statusLabel}
                    </span>
                ),
            },
            ...(approvedByName ? [{ label: t('Approved By'), value: approvedByName }] : []),
        ];

        return (
            <div className="table-list">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="table-container-header">{t('Visitor Log Details')}</span>
                    <Button onClick={this.goBack}>{t('Go Back')}</Button>
                </div>

                <Row>
                    {/* Visitor Information */}
                    <Col md={5}>
                        <div className="form-group field field-string w-100 mb-3">
                            <label className="control-label fs15">{t('Visitor Information')}</label>
                            <table className="table table-bordered mb-0 mt-1">
                                <tbody>
                                    {infoRows.map(({ label, value }) => (
                                        <tr key={label}>
                                            <td className="text-muted font-weight-bold vld-info-label">
                                                {label}
                                            </td>
                                            <td className="vld-info-value">
                                                {value}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Col>

                    {/* Safety Responses + PM Review + Actions */}
                    <Col md={7}>
                        {logDetail.safetyResponses && logDetail.safetyResponses.length > 0 && (
                            <div className="form-group field field-string w-100 mb-3">
                                <label className="control-label fs15">{t('Health & Safety Responses')}</label>
                                <div className="mt-2">{this.renderSafetyResponses(logDetail.safetyResponses)}</div>
                            </div>
                        )}

                        {this.renderPmReviewSection(logDetail)}

                        {status === 'approved' && (
                            <div className="form-group field field-string w-100 mb-3">
                                <label className="control-label fs15">{t('Visitor Pass')}</label>
                                <div className="mt-2">
                                    <Button fill wd onClick={this.handleDownloadPass} disabled={downloadingPass}>
                                        <i className="fas fa-download mr-1" /> {t('Download Visitor Pass')}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {((isPending && !pmData.requires_pm_review) || canCheckout) && (
                            <div className="form-group field field-string w-100">
                                <label className="control-label fs15">{t('Actions')}</label>
                                <div className="mt-2">
                                    {isPending && !pmData.requires_pm_review && (
                                        <>
                                            <div className="form-group field field-string w-100">
                                                <label className="control-label fs15">{t('Notes (optional)')}</label>
                                                <textarea
                                                    className="form-control"
                                                    rows={3}
                                                    maxLength={500}
                                                    value={approveNotes}
                                                    onChange={(e) => this.setState({ approveNotes: e.target.value })}
                                                    placeholder={t('Add notes...')}
                                                />
                                            </div>
                                            <div className="d-flex vld-action-btns">
                                                <Button fill wd onClick={this.handleApprove} disabled={actionLoading} className="vld-action-btn">
                                                    <i className="fa fa-check" /> {t('Accept')}
                                                </Button>
                                                <Button onClick={this.handleReject} disabled={actionLoading} className="vld-action-btn">
                                                    <i className="fa fa-times" /> {t('Reject')}
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                    {canCheckout && (
                                        <Button fill wd onClick={this.handleCheckout} disabled={actionLoading}>
                                            <i className="fas fa-sign-out-alt" /> {t('Check Out')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </Col>
                </Row>
            </div>
        );
    }
}

export default inject(
    'visitorStore',
    'commonStore',
    'userStore'
)(applicationRouter(withLocalization(observer(VisitorLogDetail))));
